import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd } from '@angular/router';
import { emptyAboutObj, emptyEducationObj, emptyExperienceObj } from '@app/account/profile/user-profile.variables';
import { EducationSchool, UserProfileInfo, WorkExperience } from '@app/core/services/bff/user-profile.service';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { EmptyComponent, EmptyProps } from '@core/components/empty/empty.component';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { TitleModule } from '@core/components/title/title.module';
import { vimeoEmbedUrl, youtubeEmbedUrl } from '@core/helpers/video';
import { PipesModule } from '@core/pipes/pipes.module';
import { AccountService, FilesService } from '@core/services';
import { loadMe } from '@store/actions/profile.actions';

import { FileTypeEnum, ImageSizeEnum, ProfileMediaModeEnum } from 'lingo2-models';
import { OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { takeUntil } from 'rxjs/operators';
import { BaseUserProfileSubpageComponent } from '../base-user-profile-subpage.component';

@Component({
  selector: 'app-user-profile-about-subpage',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    EmptyComponent,
    TitleModule,
    PipesModule,
    OnUiCoverModule,
    ModalModule,
    MobileModalModule,
  ],
  templateUrl: './user-profile-about-subpage.component.html',
  styleUrls: ['./user-profile-about-subpage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileAboutSubpageComponent extends BaseUserProfileSubpageComponent implements OnInit {
  @Input() public limited: boolean;
  @Input() public isModal: boolean;
  @Input() public set selectedSubjectId(selectedSubjectId: string) {
    this._selectedSubjectId = selectedSubjectId;
    this.init();
  }

  public get selectedSubjectId(): string {
    return this._selectedSubjectId;
  }

  @Input()
  public set profile(data: UserProfileInfo) {
    if (data) {
      this._profile = data;
      this.prepareMedia();
      this.prepareCardByProfile();
    }
  }

  @Input()
  public set id(id: string) {
    this._profile = null;
    this._id = id;
    if (this._id) {
      this.userProfileBff
        .getUserProfileInfo({ id: this._id })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (profile) => {
            this._profile = profile;
            this.prepareMedia();
            this.prepareCardByProfile();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
            this.store.dispatch(loadMe());
          },
        });
    }
  }

  public get id(): string {
    return this._id;
  }

  @Output() public loaded = new EventEmitter<boolean>();

  private _id: string;
  public about: string;
  public workExperience: WorkExperience[] = [];
  public educationSchools: EducationSchool[] = [];
  public isMediaPrepared = false;
  public isVideoFromYoutube = false;
  public safeMediaUrl: SafeResourceUrl;
  private _selectedSubjectId: string;
  private subjectId: string;

  public emptyAboutObj: Partial<EmptyProps>;
  public emptyEducationObj = emptyEducationObj;
  public emptyExperienceObj = emptyExperienceObj;

  public certificateModalOpen = false;
  public indexCertificate: number;
  public certificates = [];
  public reachedPrev = false;
  public reachedNext = false;

  ngOnInit(): void {
    this.init();
    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (event) => {
        if (event instanceof NavigationEnd) {
          const routeArr = this.router.url.split('/');
          this.subjectId = routeArr[routeArr.length - 1];
          this.prepareMedia();
          this.prepareCardByProfile();
          this.detectChanges();
        }
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.prepareMedia();
      },
    });
  }

  private init() {
    this.loading = false;
    this.subjectId = this.selectedSubjectId ? this.selectedSubjectId : this.subjectId;
    this.prepareMedia();
    this.prepareCardByProfile();
  }

  public checkReached() {
    this.reachedPrev = this.indexCertificate === 0;
    this.reachedNext = this.indexCertificate === this.certificates?.length - 1;
    this.detectChanges();
  }

  public togglePrev() {
    this.indexCertificate--;
    this.checkReached();
  }

  public toggleNext() {
    this.indexCertificate++;
    this.checkReached();
  }

  protected prepareCardByProfile() {
    this.about = this._profile?.about;
    if (!this.isTeacher()) {
      emptyAboutObj.image = 'about-student';
    }

    this.emptyAboutObj = emptyAboutObj;
    this.workExperience = this._profile?.workExperiences;
    this.educationSchools = this._profile?.educationSchools || [];
    this.certificates = this._profile?.educationDocs || [];
    this.loaded.emit(true);
    this.detectChanges();
  }

  public getCertificateCover(cover_id: string): OnUiCover {
    return {
      type: 'cover',
      img: FilesService.getFileUrlBySize(cover_id, ImageSizeEnum.wide),
      form: 'base',
    };
  }

  public isAuthor() {
    return this._me && this._user && this._me?.id === this._user.id;
  }

  public isTeacher(): boolean {
    return AccountService.isAsIfTeacher(this._user);
  }

  public prepareMedia() {
    const teachingSubject = this._profile?.teachingSubjects?.find((el) => el.subjectId.toString() == this.subjectId);

    if (!teachingSubject || (!teachingSubject.videoUrl && !teachingSubject.videoId)) {
      this.safeMediaUrl = null;
      this.isMediaPrepared = false;
      return;
    }

    if (
      teachingSubject.videoId &&
      (teachingSubject.videoMode[0] || teachingSubject.videoMode) === ProfileMediaModeEnum.use_media_id
    ) {
      this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        FilesService.getFileUrlBySize(teachingSubject.videoId),
      );
      this.isVideoFromYoutube = false;
      this.isMediaPrepared = true;
      this.detectChanges();
    } else if (
      teachingSubject.videoUrl &&
      (teachingSubject.videoMode[0] || teachingSubject.videoMode) === ProfileMediaModeEnum.use_media_url
    ) {
      this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        youtubeEmbedUrl(teachingSubject.videoUrl) || vimeoEmbedUrl(teachingSubject.videoUrl),
      );
      this.isMediaPrepared = true;
      this.isVideoFromYoutube = true;
      this.detectChanges();
    } else {
      this.safeMediaUrl = null;
      this.isMediaPrepared = false;
    }
  }

  public openCertificateModal(id: string, index) {
    this.filesService
      .getFileById(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (file) => {
          if (file.type === FileTypeEnum.image) {
            this.certificateModalOpen = true;
            this.indexCertificate = index;
          } else {
            window.open(file.storage_file_url, '_blank');
          }
          this.checkReached();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-FILE');
        },
      });
  }

  public trackByFn(index) {
    return index;
  }
}
